import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { RestService } from './rest.service';
import { ICurrentSelectedProperty } from './user.service';
import { UserPropertyService } from './user-property.service';
import { IFlaw, IFlawStatusChange, IFlawStatusUserHistory } from '../../../shared/interfaces/flaw.interface';
import { $flaw, changedByType, flawStatus } from '../../../shared/constants/flaw';
import {
  IBulkTaskCreationInfoResponse,
  IFindingResponse,
  IGetFindingRulesRequestQuery,
  IGetBulkTaskCreationInfoRequestQuery,
  IGetDigitalAssetFindingsRequestQuery,
} from '../../../shared/interfaces/finding.interface';
import { IFindingsDismissRestoreRequest } from '../../../shared/interfaces/ignore.interface';
import { IAiFiltersResponse, IGetAiFiltersRequestQuery } from '../../../shared/interfaces/ai-filters.interface';
import { IAuditRuleServerResponse, IGetTaskDescriptionForRuleResponse } from '../../../shared/interfaces/audit-rule.interface';
import { AccessibilityAuditToolNames } from '../../../shared/constants/audit-tool';

@Injectable({
  providedIn: 'root',
})
export class FlawService {
  constructor(
    private restService: RestService,
    private userService: UserPropertyService,
  ) {}

  public setViewed(flawId: string): Observable<void> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.markFlawAsViewed(workspaceId, digitalPropertyId, flawId),
        ),
      );
  }

  public getHistory(flawId: string): Observable<IFlawStatusUserHistory[]> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.getFlawHistory(workspaceId, digitalPropertyId, flawId),
        ),
      );
  }

  public hasHistory(flaw: IFlaw): boolean {
    return (
      flaw[$flaw.statusHistory].some(
        (changeEvent: IFlawStatusChange): boolean => changeEvent.changedByType === changedByType.user,
      ) || false
    );
  }

  public getPreviousStatus(flaw: IFlaw): flawStatus {
    const newFlawStatusHistoryLength: number = 1;
    const isNewFlaw: boolean = flaw[$flaw.statusHistory].length <= newFlawStatusHistoryLength;

    if (isNewFlaw) {
      return flawStatus.open;
    }
    const secondLastStatusIndex: number = flaw[$flaw.statusHistory].length - 2;
    return flaw[$flaw.statusHistory][secondLastStatusIndex].newStatus;
  }

  public getForDigitalAsset(request: IGetDigitalAssetFindingsRequestQuery): Observable<IFindingResponse> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.getDigitalPropertyFindings(workspaceId, digitalPropertyId, request),
        ),
      );
  }

  public getBulkTaskCreateInfo(request: IGetBulkTaskCreationInfoRequestQuery): Observable<IBulkTaskCreationInfoResponse> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.getBulkTaskCreateInfo(workspaceId, digitalPropertyId, request),
        ),
      );
  }

  public getAiFiltersForDigitalAsset(request: IGetAiFiltersRequestQuery): Observable<IAiFiltersResponse> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.getAiFiltersForDigitalProperty(workspaceId, digitalPropertyId, request),
        ),
      );
  }

  public dismissFindings(ignoreRequest: IFindingsDismissRestoreRequest): Observable<number> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.dismissFindings(workspaceId, digitalPropertyId, ignoreRequest),
        ),
      );
  }

  public restoreFindings(restoreRequest: IFindingsDismissRestoreRequest): Observable<number> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.restoreFindings(workspaceId, digitalPropertyId, restoreRequest),
        ),
      );
  }

  public getFindingRules(request: IGetFindingRulesRequestQuery): Observable<IAuditRuleServerResponse> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.getFindingRules(workspaceId, digitalPropertyId, request),
        ),
      );
  }

  public getTaskDescriptionForRule(
    ruleId: string,
    tool: AccessibilityAuditToolNames,
  ): Observable<IGetTaskDescriptionForRuleResponse> {
    return this.userService
      .currentSelectedProperty()
      .pipe(
        mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty) =>
          this.restService.getTaskDescriptionForRule(workspaceId, digitalPropertyId, ruleId, tool),
        ),
      );
  }
}
